import React, { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { useSideNav } from '@/hooks/useSideNav';

const sidebar = css`
  display: flex;
  flex-direction: column;
  width: 240px;
  min-width: 240px;
  padding: 32px 0;
  justify-content: space-between;
  background: #fff;
  height: 100vh;
  position: sticky;
  top: 0;
`;

const topSection = css`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const logoSection = css`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 24px;
  cursor: pointer;
`;

const navGroups = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 0 16px;
`;

const navGroupNoIcons = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const navGroupWithIcons = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const navItemBase = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 8px;
  border-radius: 6px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #251b18;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: background var(--grn-transition-fast, 200ms) ease;

  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }

  svg {
    flex-shrink: 0;
    color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  }
`;

const navItemNoIcons = css`
  ${navItemBase};
  height: 36px;
  font-weight: 400;
`;

const navItemWithIcons = css`
  ${navItemBase};
  height: 40px;
  font-weight: 400;
`;

const navItemActiveNoIcons = css`
  ${navItemBase};
  height: 36px;
  font-weight: 500;
  background: #f8f7f7;

  &:hover {
    background: #f8f7f7;
  }
`;

const navItemActiveWithIcons = css`
  ${navItemBase};
  height: 40px;
  font-weight: 500;
  background: #f8f7f7;

  &:hover {
    background: #f8f7f7;
  }
`;

const profileRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 24px;
  border-radius: 50px;
  cursor: pointer;
`;

const profileImage = css`
  width: 32px;
  height: 32px;
  border-radius: 500px;
  border: 1px solid rgba(55, 49, 47, 0.12);
  object-fit: cover;
  flex-shrink: 0;
`;

const chevronStyle = css`
  color: rgba(61, 46, 41, 0.52);
`;

const iconWrap = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
`;

/* eslint-disable react/no-multi-comp */
function IconHome() {
  return (
    <svg css={iconWrap} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M13.75 0C14.9926 0 16 1.00736 16 2.25V13.75C16 14.9919 14.9943 16 13.751 16H2.24902C1.00567 16 0 14.9919 0 13.75V2.25C0 1.00736 1.00736 0 2.25 0H13.75ZM1.5 13.75C1.5 14.1649 1.83552 14.5 2.24902 14.5H7.25V8.75H1.5V13.75ZM8.75 14.5H13.751C14.1645 14.5 14.5 14.1649 14.5 13.75V2.25C14.5 1.83579 14.1642 1.5 13.75 1.5H8.75V14.5ZM2.25 1.5C1.83579 1.5 1.5 1.83579 1.5 2.25V7.25H7.25V1.5H2.25Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconMail() {
  return (
    <svg css={iconWrap} width="17" height="14" viewBox="0 0 17 14" fill="none">
      <path
        d="M14.75 0C15.9926 0 17 1.00736 17 2.25V11.75C17 12.9926 15.9926 14 14.75 14H2.25C1.00736 14 0 12.9926 0 11.75V2.25C0 1.00736 1.00736 0 2.25 0H14.75ZM9.62695 7.69922C8.92985 8.10264 8.07018 8.10257 7.37305 7.69922L1.5 4.30078V11.75C1.5 12.1642 1.83579 12.5 2.25 12.5H14.75C15.1642 12.5 15.5 12.1642 15.5 11.75V4.30078L9.62695 7.69922ZM2.25 1.5C1.83579 1.5 1.5 1.83579 1.5 2.25V2.56641L8.12402 6.40137C8.35638 6.53575 8.64365 6.53582 8.87598 6.40137L15.5 2.56641V2.25C15.5 1.83579 15.1642 1.5 14.75 1.5H2.25Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconForms() {
  return (
    <svg css={iconWrap} width="17" height="10" viewBox="0 0 17 10" fill="none">
      <path
        d="M16.25 8.5C16.6642 8.5 17 8.83579 17 9.25C17 9.66421 16.6642 10 16.25 10H0.75C0.335789 10 0 9.66421 0 9.25C0 8.83579 0.335786 8.5 0.75 8.5H16.25ZM16.25 4.25C16.6642 4.25 17 4.58579 17 5C17 5.41421 16.6642 5.75 16.25 5.75H0.75C0.335786 5.75 0 5.41421 0 5C0 4.58579 0.335786 4.25 0.75 4.25H16.25ZM16.25 0C16.6642 0 17 0.335786 17 0.75C17 1.16421 16.6642 1.5 16.25 1.5H0.75C0.335788 1.5 0 1.16421 0 0.75C0 0.335786 0.335786 0 0.75 0H16.25Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconWorkflows() {
  return (
    <svg css={iconWrap} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 0C9.51873 0 10.75 1.23117 10.75 2.75C10.75 4.00844 9.9037 5.06578 8.75 5.39258V7.25H11.75C12.9926 7.25 14 8.25736 14 9.5V10.6064C15.1538 10.9332 16 11.9915 16 13.25C16 14.7688 14.7687 16 13.25 16C11.7312 16 10.5 14.7688 10.5 13.25C10.5 11.9915 11.3461 10.9332 12.5 10.6064V9.5C12.5 9.08579 12.1642 8.75 11.75 8.75H4.25C3.83579 8.75 3.5 9.08579 3.5 9.5V10.6064C4.65383 10.9332 5.5 11.9915 5.5 13.25C5.5 14.7688 4.26873 16 2.75 16C1.23119 16 0 14.7688 0 13.25C0 11.9915 0.846128 10.9332 2 10.6064V9.5C2 8.25736 3.00736 7.25 4.25 7.25H7.25V5.39258C6.09626 5.06578 5.25 4.00843 5.25 2.75C5.25 1.23119 6.48119 0 8 0ZM2.75 12C2.05961 12 1.5 12.5596 1.5 13.25C1.5 13.9404 2.05962 14.5 2.75 14.5C3.44033 14.5 4 13.9404 4 13.25C4 12.5596 3.44033 12 2.75 12ZM13.25 12C12.5596 12 12 12.5596 12 13.25C12 13.9404 12.5596 14.5 13.25 14.5C13.9403 14.5 14.5 13.9404 14.5 13.25C14.5 12.5596 13.9403 12 13.25 12ZM8 1.5C7.30961 1.5 6.75 2.05962 6.75 2.75C6.75 3.44038 7.30961 4 8 4C8.69033 4 9.25 3.44037 9.25 2.75C9.25 2.05963 8.69033 1.5 8 1.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconCheckouts() {
  return (
    <svg css={iconWrap} width="17" height="14" viewBox="0 0 17 14" fill="none">
      <path
        d="M14.75 0C15.9926 0 17 1.00736 17 2.25V11.75C17 12.9926 15.9926 14 14.75 14H2.25C1.00736 14 0 12.9926 0 11.75V2.25C0 1.00736 1.00736 0 2.25 0H14.75ZM1.5 11.75C1.5 12.1642 1.83579 12.5 2.25 12.5H14.75C15.1642 12.5 15.5 12.1642 15.5 11.75V5.5H1.5V11.75ZM2.25 1.5C1.83579 1.5 1.5 1.83579 1.5 2.25V4H15.5V2.25C15.5 1.83579 15.1642 1.5 14.75 1.5H2.25ZM3.25 2C3.66421 2 4 2.33579 4 2.75C4 3.16421 3.66421 3.5 3.25 3.5C2.83579 3.5 2.5 3.16421 2.5 2.75C2.5 2.33579 2.83579 2 3.25 2ZM5.75 2C6.16421 2 6.5 2.33579 6.5 2.75C6.5 3.16421 6.16421 3.5 5.75 3.5C5.33579 3.5 5 3.16421 5 2.75C5 2.33579 5.33579 2 5.75 2ZM8.25 2C8.66421 2 9 2.33579 9 2.75C9 3.16421 8.66421 3.5 8.25 3.5C7.83579 3.5 7.5 3.16421 7.5 2.75C7.5 2.33579 7.83579 2 8.25 2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconProducts() {
  return (
    <svg css={iconWrap} width="14" height="17" viewBox="0 0 14 17" fill="none">
      <path
        d="M6.99969 0C9.04146 0 10.7249 1.52995 10.9684 3.50586C12.3793 3.56918 13.5226 4.69482 13.5905 6.11914L13.9968 14.6426C14.0579 15.9258 13.0344 17 11.7497 17H2.24969C0.965036 16.9998 -0.0587167 15.9258 0.00261923 14.6426L0.410822 6.11816C0.479119 4.69493 1.62135 3.57021 3.03094 3.50586C3.27441 1.53002 4.95804 0.000165407 6.99969 0ZM4.49969 5.75C4.49969 6.16421 4.1639 6.5 3.74969 6.5C3.33563 6.49982 2.99969 6.1641 2.99969 5.75V5.01074C2.40609 5.08602 1.93922 5.57771 1.90985 6.19043L1.50067 14.7139C1.48017 15.1416 1.82156 15.4998 2.24969 15.5H11.7497C12.1779 15.5 12.5191 15.1416 12.4987 14.7139L12.0925 6.19043C12.0632 5.57682 11.5946 5.08404 10.9997 5.00977V5.75C10.9997 6.16421 10.6639 6.5 10.2497 6.5C9.83563 6.49982 9.49969 6.1641 9.49969 5.75V5H4.49969V5.75ZM6.99969 1.5C5.79032 1.50016 4.78107 2.35893 4.54949 3.5H9.44988C9.21824 2.35889 8.20916 1.5 6.99969 1.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconAudience() {
  return (
    <svg css={iconWrap} width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path
        d="M8.5 0C13.1944 0 17 3.80558 17 8.5C17 13.1944 13.1944 17 8.5 17C3.80558 17 0 13.1944 0 8.5C0 3.80558 3.80558 0 8.5 0ZM8.5 1.5C4.63401 1.5 1.5 4.63401 1.5 8.5C1.5 12.366 4.63401 15.5 8.5 15.5C12.366 15.5 15.5 12.366 15.5 8.5C15.5 4.63401 12.366 1.5 8.5 1.5ZM11.3223 10.6787C11.4997 10.3045 11.947 10.145 12.3213 10.3223C12.6955 10.4997 12.855 10.947 12.6777 11.3213C12.4414 11.8201 12.0903 12.2688 11.6484 12.6416C10.795 13.3616 9.66117 13.75 8.5 13.75C7.33883 13.75 6.20504 13.3616 5.35156 12.6416C4.90969 12.2688 4.55859 11.8201 4.32227 11.3213C4.14497 10.947 4.30452 10.4997 4.67871 10.3223C5.05298 10.145 5.50034 10.3045 5.67773 10.6787C5.81698 10.9727 6.03093 11.2526 6.31836 11.4951C6.87859 11.9678 7.66196 12.25 8.5 12.25C9.33804 12.25 10.1214 11.9678 10.6816 11.4951C10.9691 11.2526 11.183 10.9727 11.3223 10.6787ZM5.5 5.5C6.32843 5.5 7 6.17157 7 7C7 7.82843 6.32843 8.5 5.5 8.5C4.67157 8.5 4 7.82843 4 7C4 6.17157 4.67157 5.5 5.5 5.5ZM11.5 5.5C12.3284 5.5 13 6.17157 13 7C13 7.82843 12.3284 8.5 11.5 8.5C10.6716 8.5 10 7.82843 10 7C10 6.17157 10.6716 5.5 11.5 5.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconAnalytics() {
  return (
    <svg
      css={[
        iconWrap,
        css`
          transform: rotate(90deg);
        `,
      ]}
      width="17"
      height="12"
      viewBox="0 0 17 12"
      fill="none"
    >
      <path
        d="M16.25 10.5C16.6642 10.5 17 10.8358 17 11.25C17 11.6642 16.6642 12 16.25 12H10.25C9.83579 12 9.5 11.6642 9.5 11.25C9.5 10.8358 9.83579 10.5 10.25 10.5H16.25ZM16.25 5.25C16.6642 5.25 17 5.58579 17 6C17 6.41421 16.6642 6.75 16.25 6.75H0.75C0.335786 6.75 0 6.41421 0 6C0 5.58579 0.335786 5.25 0.75 5.25H16.25ZM16.25 0C16.6642 0 17 0.335788 17 0.75C17 1.16421 16.6642 1.5 16.25 1.5H4.25293C3.83872 1.5 3.50293 1.16421 3.50293 0.75C3.50293 0.335787 3.83872 0 4.25293 0H16.25Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function SideNav() {
  const [, setSideNav] = useSideNav();
  const [showIcons, setShowIcons] = useState(false);
  const closeSideNav = useCallback(() => setSideNav(false), [setSideNav]);
  const toggleIcons = useCallback(() => setShowIcons(prev => !prev), []);

  const groupStyle = showIcons ? navGroupWithIcons : navGroupNoIcons;
  const itemStyle = showIcons ? navItemWithIcons : navItemNoIcons;
  const activeStyle = showIcons ? navItemActiveWithIcons : navItemActiveNoIcons;

  return (
    <aside css={sidebar}>
      <div css={topSection}>
        <div
          css={logoSection}
          role="button"
          tabIndex={0}
          onClick={closeSideNav}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') closeSideNav();
          }}
          aria-label="Back to top navigation"
        >
          <svg height="14" viewBox="0 0 57 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.3188 3.02031C16.9384 3.02031 18.8421 4.93639 18.8421 7.51821C18.8421 10.1001 16.9384 12 14.3188 12C11.6667 12 9.81182 10.1001 9.81182 7.51821C9.81182 4.93639 11.6667 3.02031 14.3188 3.02031ZM14.3188 4.74153C12.7568 4.74153 11.748 5.92696 11.748 7.502C11.748 9.12584 12.7568 10.295 14.3188 10.295C15.8971 10.295 16.8733 9.10952 16.8733 7.502C16.8733 5.94317 15.8808 4.74153 14.3188 4.74153Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M35.1292 3.02031C37.944 3.02031 39.4735 5.08255 39.4735 7.59945C39.4735 7.7131 39.4735 7.92421 39.4573 8.08661H32.6235C32.6886 9.4993 33.8601 10.3599 35.2268 10.3599C36.1542 10.3599 36.9353 9.9377 37.4234 9.12586L39.3108 9.46685C38.7739 11.0906 36.984 11.9837 35.1455 11.9837C32.5096 11.9837 30.6873 10.1326 30.6873 7.51821C30.6873 4.96891 32.5259 3.02033 35.1292 3.02031ZM35.1292 4.61166C33.795 4.61167 32.8512 5.43991 32.656 6.60902H37.4559C37.2769 5.35866 36.4471 4.61166 35.1292 4.61166Z"
              fill="#111110"
            />
            <path
              d="M44.0942 2.9878C45.9002 2.9878 47.1695 3.97828 47.2996 5.55337L45.461 5.6184C45.3146 4.98506 44.8101 4.53042 43.9966 4.53042C43.1994 4.53042 42.7275 4.9201 42.7275 5.47213C42.7275 5.92685 43.0041 6.13794 43.6875 6.3653L45.3308 6.9336C46.6975 7.40454 47.4949 7.9566 47.4949 9.25564C47.4949 11.0256 46.0467 11.9836 44.0942 11.9836C42.1254 11.9836 40.7749 11.0256 40.6448 9.33679L42.581 9.25564C42.6299 9.88892 43.1505 10.4735 44.1105 10.4735C45.0054 10.4735 45.4934 10.0513 45.4934 9.46676C45.4934 8.89844 45.1193 8.70355 44.2081 8.39508L43.0041 8.00528C41.6211 7.56688 40.775 6.90115 40.775 5.63461C40.775 4.0433 42.1091 2.9878 44.0942 2.9878Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M28.9135 11.7721H27.1074V11.155C27.1074 10.9276 27.1074 10.749 27.14 10.6029H27.0749C26.489 11.4635 25.4478 11.9669 24.2112 11.9669C21.7706 11.9669 20.0947 10.0833 20.0947 7.53396C20.0947 4.98458 21.7705 3.03599 24.1298 3.03596C25.3827 3.03596 26.4241 3.57189 26.9772 4.35136H27.0261C27.0098 3.45824 26.9935 3.23089 26.9935 2.22413V0.0481693H28.9135V11.7721ZM24.5691 4.77349C23.0072 4.7735 22.0309 6.00759 22.0309 7.51766C22.0309 9.06033 23.0234 10.2781 24.5691 10.2781C26.0986 10.2781 27.1074 9.07655 27.1074 7.50145C27.1074 5.95883 26.0986 4.77349 24.5691 4.77349Z"
              fill="#111110"
            />
            <path
              d="M4.03511 0C4.21413 0 4.39312 4.0185e-05 4.60462 0.0325143V1.70501C4.53955 1.68875 4.49066 1.6888 4.40935 1.6888C3.69348 1.68881 3.22165 1.93235 3.12406 2.6143C3.10776 2.76049 3.10776 2.85791 3.10776 2.98789V3.23142H4.60462V4.85519H3.12406V11.7726H1.20405V4.85519H0V3.23142H1.20405V2.93916C1.20405 1.10429 2.03387 1.08386e-05 4.03511 0Z"
              fill="#111110"
            />
            <path d="M8.03733 11.772H6.08481V0.0481693H8.03733V11.772Z" fill="#111110" />
            <path
              d="M51.1215 2.67868C51.1215 4.10764 51.089 5.00074 51.0727 6.26729L51.089 6.2836C51.691 5.63411 52.2442 5.14693 52.8624 4.51365L54.1153 3.23087H56.7675L52.2279 7.25782L56.9139 11.772H54.1642C53.0903 10.7003 52.0489 9.57995 51.1053 8.52449H51.089C51.1053 9.23892 51.1215 9.82351 51.1215 10.6353V11.772H49.2179V0.0481693H51.1215V2.67868Z"
              fill="#111110"
            />
          </svg>
        </div>

        <div css={navGroups}>
          <div css={groupStyle}>
            <span css={itemStyle}>
              {showIcons && <IconHome />}
              Home
            </span>
            <span css={activeStyle}>
              {showIcons && <IconMail />}
              Emails
            </span>
            <span css={itemStyle}>
              {showIcons && <IconForms />}
              Forms
            </span>
            <span css={itemStyle}>
              {showIcons && <IconWorkflows />}
              Workflows
            </span>
            <span css={itemStyle}>
              {showIcons && <IconCheckouts />}
              Checkouts
            </span>
          </div>
          <div css={groupStyle}>
            <span css={itemStyle}>
              {showIcons && <IconProducts />}
              Products
            </span>
            <span css={itemStyle}>
              {showIcons && <IconAudience />}
              Audience
            </span>
            <span
              css={itemStyle}
              role="button"
              tabIndex={0}
              onClick={toggleIcons}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') toggleIcons();
              }}
            >
              {showIcons && <IconAnalytics />}
              Analytics
            </span>
          </div>
        </div>
      </div>

      <div
        css={profileRow}
        role="button"
        tabIndex={0}
        onClick={() => setSideNav(false)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') setSideNav(false);
        }}
      >
        <img css={profileImage} src="/images/profile.png" alt="Profile" />
        <svg css={chevronStyle} width="10" height="18" viewBox="0 0 10 18" fill="none">
          <path
            d="M1 7L5 11L9 7"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </aside>
  );
}
